import React, { createContext, useReducer } from "react";
import taxonomy from "../data/taxonomy.json";
import { resolveBrand } from "../utils/brandResolver";
import { applyBrandTheme } from "../utils/applyBrandTheme";
import { extractLocaleFromPath } from "../i18n";
import {
  brandLocations as resolveBrandLocations,
  resolveInitialLocation,
  writeStoredLocation,
  isAllowedLocation,
} from "../utils/locationResolver";

// Resolve brand from hostname
const brand = resolveBrand(window.location.hostname + (window.location.port ? ':' + window.location.port : ''));
// Apply brand colors to :root SYNCHRONOUSLY, before React's first paint —
// the old useEffect-only path let one frame render with the Ocean Teal
// defaults from index.css (first-load color flash, fixed 2026-07-17).
applyBrandTheme(brand);
// Resolve locale from the URL SYNCHRONOUSLY too. The old `locale: "en"` seed
// only flipped inside App.js's post-mount useEffect, so the FIRST render of
// every /es|/pt|/ko|/ja|/fr route hydrated the prerendered localized HTML with
// English strings — visible English leakage on non-EN pages (fixed 2026-07-27).
const initialLocale = extractLocaleFromPath(window.location.pathname).locale || "en";

// Resolve the visitor's clinic SYNCHRONOUSLY too (same precedent as the brand
// theme + locale above): URL location (authoritative — deep-linking to
// /raleigh/... IS Raleigh context) → brand-scoped localStorage → null, which
// means "not yet known" and makes LocationGate ask. Any stored/URL slug is
// validated against the CURRENT brand's allowed slugs, so a stale gso slug can
// never apply on apexosn.com. Storage access is try/catch guarded inside the
// resolver (private mode must not crash the app).
const allowedLocations = resolveBrandLocations(brand, taxonomy);
const initialLocationSlug = resolveInitialLocation(
  window.location.pathname,
  brand.brandKey,
  allowedLocations
);

const initialState = {
  // Brand config (resolved from domain)
  brand: brand,
  // Legacy alias
  client: {
    ...brand,
    name: brand.name,
    shortName: brand.shortName,
    phone: brand.phone,
    phoneExisting: brand.phoneExisting || null,
    email: brand.email,
    logo: brand.logo,
    colors: brand.colors,
    schema: brand.schema,
    socialLinks: brand.socialLinks,
    defaultLocale: "en",
    supportedLocales: ["en", "es", "pt", "ko", "ja", "fr"],
  },
  taxonomy: taxonomy,

  // UI state
  isMobile: false,
  isMobileMenuOpen: false,

  // Locale (seeded from the URL prefix — see initialLocale above)
  locale: initialLocale,

  // Selected clinic slug (seeded above; null = ask the visitor)
  selectedLocationSlug: initialLocationSlug,

  // Page data cache
  pageCache: {},

  // Loading states
  isLoading: false,
  error: null,
};

function appReducer(state, action) {
  switch (action.type) {
    case "SET_IS_MOBILE":
      return { ...state, isMobile: action.payload };

    case "SET_MOBILE_MENU":
      return { ...state, isMobileMenuOpen: action.payload };

    case "TOGGLE_MOBILE_MENU":
      return { ...state, isMobileMenuOpen: !state.isMobileMenuOpen };

    case "SET_LOCALE":
      return { ...state, locale: action.payload };

    case "SET_SELECTED_LOCATION": {
      const allowed = resolveBrandLocations(state.brand, state.taxonomy);
      // Never trust a foreign/unknown slug; clearing (null) is always allowed.
      if (action.payload && !isAllowedLocation(action.payload, allowed)) {
        return state;
      }
      writeStoredLocation(state.brand?.brandKey, action.payload, allowed);
      return { ...state, selectedLocationSlug: action.payload || null };
    }

    case "SET_BRAND":
      return { ...state, brand: action.payload, client: { ...state.client, ...action.payload } };

    case "SET_PAGE_DATA":
      return {
        ...state,
        pageCache: {
          ...state.pageCache,
          [action.payload.slug]: action.payload.data,
        },
      };

    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "CLEAR_ERROR":
      return { ...state, error: null };

    default:
      return state;
  }
}

export const ApplicationContext = createContext();

export function ApplicationContextProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <ApplicationContext.Provider value={{ state, dispatch }}>
      {children}
    </ApplicationContext.Provider>
  );
}
